import { type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  LogOut,
  Palette,
  Settings as SettingsIcon,
  Users2,
  Package,
  Shield,
  Webhook,
  LayoutDashboard,
  Sparkles,
  Wallet,
  ShieldCheck,
  Mail,
  Inbox,
  Bell,
  Box,
  FileCode2,
  ShoppingCart,
  Megaphone,
} from 'lucide-react';
import type { ApiOrganization, ApiUser } from '@/lib/api-client';
import { useLogout } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { ImpersonationBanner } from './ImpersonationBanner';
import { cn } from '@/lib/utils';

const platformNav = [
  { to: '/platform', icon: LayoutDashboard, label: 'Kontrol Paneli', exact: true },
  { to: '/platform/theme', icon: Palette, label: 'Tema' },
  { to: '/platform/general', icon: SettingsIcon, label: 'Genel Ayarlar' },
  { to: '/platform/ai-providers', icon: Sparkles, label: 'AI Sağlayıcılar' },
  { to: '/platform/oauth', icon: Shield, label: 'OAuth Sağlayıcılar' },
  { to: '/platform/users', icon: Users2, label: 'Kullanıcılar' },
  { to: '/platform/orgs', icon: Package, label: 'Organizasyonlar' },
  { to: '/platform/webhooks', icon: Webhook, label: 'Stripe Ayarları' },
  { to: '/platform/mailer', icon: Mail, label: 'E-posta Ayarları' },
  { to: '/platform/inbox', icon: Inbox, label: 'Gelen Kutusu' },
  { to: '/platform/notifications', icon: Bell, label: 'Bildirimler' },
  { to: '/platform/packages', icon: Box, label: 'Paketler' },
  { to: '/platform/announcements', icon: Megaphone, label: 'Duyurular' },
  { to: '/platform/prompt-templates', icon: FileCode2, label: 'Prompt Şablonları' },
  { to: '/platform/crypto-wallets', icon: Wallet, label: 'Kripto Cüzdanlar' },
  { to: '/platform/carts', icon: ShoppingCart, label: 'Sepet Onay Kuyruğu' },
  { to: '/platform/explorers', icon: ShieldCheck, label: 'Blockchain Explorer\'lar' },
];

export function PlatformLayout({
  user,
  orgs: _orgs,
  activeOrgId: _activeOrgId,
  children,
}: {
  user: ApiUser;
  orgs: ApiOrganization[];
  activeOrgId: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const logout = useLogout();
  const router = useRouterState();
  const currentPath = router.location.pathname;

  const onLogout = async () => {
    await logout.mutateAsync();
    navigate({ to: '/login' });
  };

  return (
    <div className="min-h-screen bg-background">
      <ImpersonationBanner />
      <header className="border-b bg-background/80 backdrop-blur sticky top-0 z-30">
        <div className="flex items-center justify-between h-14 px-4 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard">
                <ArrowLeft className="w-4 h-4" />
                <span>Uygulamaya dön</span>
              </Link>
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-primary" />
              <span className="font-semibold">Platform Yönetimi</span>
              <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                Owner
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm text-muted-foreground hidden sm:block">{user.email}</div>
            <Button variant="ghost" size="icon" onClick={onLogout} title="Çıkış">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="w-64 border-r h-[calc(100vh-3.5rem)] sticky top-14 bg-sidebar text-sidebar-foreground">
          <div className="p-4">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Platform Ayarları
            </h2>
            <nav className="space-y-1">
              {platformNav.map((item) => {
                const active = item.exact ? currentPath === item.to : currentPath.startsWith(item.to);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                      active
                        ? 'bg-sidebar-primary text-sidebar-primary-foreground font-medium'
                        : 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                    )}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="flex-1 p-6 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
